import { Component, effect, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ContactTypeEnum } from '../../../../../../../../types/src/common/enum/contact-type.enum';
import { AddressDetailObject, PersonContactGridItemObject, UserGridItemObject } from '@it2go/types';
import { AbstractControl, AsyncValidatorFn, ReactiveFormsModule, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Router } from '@angular/router';
import { isInvalid } from '../../../../shared/helpers/validate';
import { debounceTime } from 'rxjs/operators';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import config from '../../../../../config/config';
import { FilterBuilder } from '../../../helper/filter.builder';
import { PersonContactUpsertDialogStore } from './person-contact-upsert-dialog.store';
import { selectFolder } from '../../../store/person/person.selectors';
import { selectCurrentOrganizationId } from '../../../../../store/global/global.selectors';
import { FieldErrorFactory } from '../../../../shared/service/field-error.service';
import { fb, isAllowed, translate } from '../../../../shared/shared.global-service';
import { PersonContactUpsertDialogStore2 } from './person-contact-upsert-dialog.store2';
import { AppFormGroup, changeSignal } from '../../../../../utils/form.utils';
import { LinkButtonComponent } from '../../../../../components/kit/button/link-button.component';
import { PluralizationComponent } from '../../../../../components/kit/pluralization/pluralization.component';
import { TranslatePipe } from '@ngx-translate/core';
import { BaseDialogComponent } from '../../../../../components/kit/base-dialog/base-dialog.component';
import { NgClass } from '@angular/common';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { InnerModalComponent } from '../../../../shared/components/kit/inner-modal/inner-modal.component';
import { SelectInputComponent } from '../../../../../components/kit/input/select-input/select-input.component';
import {
    SearchDoctorSpecializationComponent,
} from '../../common/search-doctor-specialization/search-doctor-specialization.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { contactTypeEnumOptions } from './person-contact-upsert-dialog.types';
import { DialogModule } from '@openng/optimus-ui/dialog';
import {
    PersonContactUpsertNameMatchDialogComponent,
} from '../person-contact-upsert-name-match-dialog/person-contact-upsert-name-match-dialog.component';
import { SelectUserComponent } from '../../common/select-user/select-user.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import {
    UserWorkplaceUpsertDialogComponent,
} from '../../user-management/user-workplace-table/user-workplace-upsert-dialog/user-workplace-upsert-dialog.component';
import { UserWorkplaceObject } from '@libs/types/src/user/object-type/workplace/user-workplace.object';
import { removeTypename } from '../../../../shared/utils/object.utils';
import { userUri } from '../../../../../utils/router.utils';
import { PersonContactTypeEnum } from '@libs/types/src/services/enum/person-contact-type.enum';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { PhoneInputComponent } from '../../../../../components/kit/input/phone-input/phone-input.component';
import { AddressInputComponent } from '../../../../../components/kit/input/address-input/address-input.component';
import { WarnFloatMessageComponent } from '../../../../../components/kit/float-message/warn-float-message.component';
import { userGridItemFrag } from './person-contact-upsert-dialog.query';
import { UserLinkTypeEnum } from '@libs/types/src/user/enum/user-link.type-enum';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import { AddressBookContactObject } from '@libs/types/src/address-book/object-type/address-book-contact.object';
import {
    AddressBookInformationTypeEnum,
} from '@libs/types/src/address-book/enum/address-book-information-type.enum';
import { addressBookContactFullDetailFrag } from '../../../../../services/types/address-book.gql-types';
import { InfoFloatMessageComponent } from '../../../../../components/kit/float-message/info-float-message.component';

@Component({
    selector: 'app-person-contact-upsert-dialog',
    styleUrls: ['./person-contact-upsert-dialog.component.scss'],
    templateUrl: 'person-contact-upsert-dialog.component.html',
    providers: [PersonContactUpsertDialogStore, PersonContactUpsertDialogStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        LinkButtonComponent,
        PluralizationComponent,
        TranslatePipe,
        ButtonComponent,
        FormatPersonPipe,
        InnerModalComponent,
        NgClass,
        SelectInputComponent,
        SearchDoctorSpecializationComponent,
        TextInputComponent,
        DateInputComponent,
        DialogModule,
        SelectUserComponent,
        ActionButtonsComponent,
        EmailInputComponent,
        PhoneInputComponent,
        AddressInputComponent,
        WarnFloatMessageComponent,
        InfoFloatMessageComponent,
    ],
})
export class PersonContactUpsertDialogComponent extends BaseDialogComponent implements OnInit {
    public readonly store = inject(PersonContactUpsertDialogStore);
    public readonly store2 = inject(PersonContactUpsertDialogStore2);
    public readonly dialogService = inject(BaseDialogService);
    public readonly actions$ = inject(Actions);
    public readonly router = inject(Router);

    protected create = false;
    protected contact: PersonContactGridItemObject | null = null;
    protected subType: ContactTypeEnum | null;
    protected typeDisabled: boolean = false;

    protected selectedUser: UserGridItemObject | null = null;
    protected selectedAddressBookContact: AddressBookContactObject | null = null;
    protected selectedAddressBookContactName = '';

    @ViewChild('selectExistingModal') selectExistingModal!: InnerModalComponent;

    protected isAuthorized: boolean = false;
    protected isDoctor: boolean = false;
    protected isContact: boolean = false;
    protected enableUserEdit: boolean = true;
    protected isUserSelected: boolean = false;
    protected prefix = 'folderContact.';
    protected workplacePrefix = `${this.prefix}doctorWorkplace.`;

    protected form = new AppFormGroup({
        id: fb.control<string | null>(null),
        existingUserId: fb.control<string | null>(null),
        customContactId: fb.control<number | null>(null),
        subType: fb.control<string | null>(null, Validators.required),
        titleBeforeName: fb.control<string | null>(null),
        name: fb.control<string | null>(null, Validators.required),
        surname: fb.control<string | null>(null, Validators.required),
        titleAfterName: fb.control<string | null>(null),
        mobile: fb.control<string | null>(null),
        phone: fb.control<string | null>(null),
        email: fb.control<string | null>(
            null,
            [Validators.email],
            [this.distinctEmail()],
        ),
        address: fb.control<AddressDetailObject | null>(null),
        clientRelation: fb.control<string | null>(
            null,
            Validators.required,
        ),
        contactCircumstance: fb.control<string | null>(null),
        info: fb.control<string | null>(null),
        purposeOfAuthorization: fb.control<string | null>(null),
        scopeOfAuthorization: fb.control<string | null>(null),
        dateOfAuthorization: fb.control<Date | null>(new Date()),
        authorizationFrom: fb.control<Date | null>(new Date()),
        authorizationTo: fb.control<Date | null>(null),
        icpOfDoctor: fb.control<string | null>(null),
        internNote: fb.control<string | null>(null),
        doctorSpecialization: fb.control<string | null>('001'),
        doctorWorkplaceId: fb.control<number | null>(null),
        doctorWorkplace: fb.control<string | null>(null),
    });

    protected existingIdByEmail = '';
    protected existingNameByEmail = '';
    protected existingUsersByName: UserGridItemObject[] = [];

    nameMatchOptions: UserGridItemObject[] = [];

    private existingUserSelected: UserGridItemObject | null = null;

    private subTypeChange = changeSignal(this.form.controls.subType);

    constructor() {
        super();

        this.subType = this.dialogConfig.data.type;
        this.typeDisabled = this.dialogConfig.data.typeDisabled;
        this.contact = this.dialogConfig.data.contact;
        this.create = !this.contact;

        effect(() => {
            if (this.subTypeChange() === ContactTypeEnum.doctor && !this.store2.isSocial()) {
                this.form.controls.icpOfDoctor.setValidators(Validators.required);
            } else {
                this.form.controls.icpOfDoctor.clearValidators();
            }
            this.form.controls.icpOfDoctor.updateValueAndValidity();
        });
    }

    get userSelectedFromExisting() {
        return this.form.value.existingUserId !== null;
    }

    get contactSelectedFromExisting() {
        return this.userSelectedFromExisting || this.selectedAddressBookContact !== null;
    }

    ngOnInit() {
        this.subs.push(
            this.form.controls.subType.valueChanges.subscribe((val) => {
                this.isContact = val === ContactTypeEnum.contact;
                this.isAuthorized = val === ContactTypeEnum.authorized;
                this.isDoctor = val === ContactTypeEnum.doctor;
                this.create && this.isDoctorAllowed(val);

                if (this.isDoctor) {
                    this.form.controls['doctorSpecialization'].setValidators([Validators.required]);
                } else {
                    this.form.controls['doctorSpecialization'].setValidators(null);
                }
            }),
            this.form.controls.existingUserId.valueChanges.subscribe((val) => {
                if (val) {
                    this.form.controls.customContactId.setValue(null, { emitEvent: false });
                    this.selectedAddressBookContact = null;
                    this.selectedAddressBookContactName = '';
                }
                this.isUserSelected = this.selectedUser !== null && val !== null;
                if (val) {
                    this.store2.api.getUserWorkplaces(FilterBuilder.filter({ userId: val })).then((wp) => {
                        this.store2.newWorkplace.set(null);
                        this.store2.userWorkplaces.set(wp?.['items'] || []);
                        this.form.patchValue({
                            doctorWorkplaceId: null,
                            doctorWorkplace: null,
                        });
                    });
                }
            }),
            this.form.controls.customContactId.valueChanges.subscribe((val) => {
                if (val) {
                    this.form.controls.existingUserId.setValue(null, { emitEvent: false });
                }
            }),
            this.form.controls.email.valueChanges
                .pipe(debounceTime(config.debounceTime))
                .subscribe(async (val) => {
                    if (val && this.create && !this.contactSelectedFromExisting) {
                        await this.searchExistingByEmail(val);
                    }
                }),
            this.form.controls.name.valueChanges
                .pipe(debounceTime(config.debounceTime))
                .subscribe((val) => {
                    this.nameMatchOptions = [];
                    if (val && this.create && !this.contactSelectedFromExisting) {
                        this.searchExistingByName();
                    }
                }),
            this.form.controls.surname.valueChanges
                .pipe(debounceTime(config.debounceTime))
                .subscribe((val) => {
                    this.nameMatchOptions = [];
                    if (val && this.create && !this.contactSelectedFromExisting) {
                        this.searchExistingByName();
                    }
                }),
        );

        if (this.subType) {
            this.form.patchValue({ subType: this.subType });
        }

        if (this.contact) {
            if (this.contact?.user?.id) {
                this.fetchUserWorkplaces(this.contact.user.id);
            }
            this.enableUserEdit = !(this.contact.user?.systemic ?? false);

            this.form.patchValue({
                subType: this.contact.subType,
                icpOfDoctor: this.contact.user.icpOfDoctor,
                doctorSpecialization: this.contact.user.doctorSpecialization || this.form.value.doctorSpecialization,
                titleBeforeName: this.contact.user.titleBeforeName,
                name: this.contact.user.name,
                surname: this.contact.user.surname,
                titleAfterName: this.contact.user.titleAfterName,
                mobile: this.contact.user.mobile,
                phone: this.contact.user.phone,
                email: this.contact.user.email,
                address: this.contact.user.address,
                clientRelation: this.contact.relation,
                contactCircumstance: this.contact.data?.circumstance,
                info: this.contact.data?.info,
                internNote: this.contact.note,
                purposeOfAuthorization: this.contact.data?.purposeOfAuthorization,
                scopeOfAuthorization: this.contact.data?.scopeOfAuthorization,
                dateOfAuthorization: this.contact.data?.dateOfAuthorization
                    ? new Date(this.contact.data.dateOfAuthorization) : null,
                authorizationFrom: this.contact.data?.authorizationFrom
                    ? new Date(this.contact.data.authorizationFrom) : null,
                authorizationTo: this.contact.data?.authorizationTo
                    ? new Date(this.contact.data.authorizationTo) : null,
                doctorWorkplaceId: this.contact.data?.doctorWorkplaceId,
            });
        }

        if (this.typeDisabled) {
            this.form.controls.subType.disable();
        }
    }

    override save(_event: Event): void | Promise<void> {
        if (this.create && !isAllowed['folderContact.create']()) return;
        if (!this.create && !isAllowed['folderContact.update']()) return;
        this.updateValidators();

        if (isInvalid(this.form)) return;

        if (this.create) {
            this.createContact().then();
        } else if (this.contact) {
            this.updateContact().then();
        }
    }

    private async createContact() {
        let userId = this.userSelectedFromExisting ? (this.form.value.existingUserId as string) : '';
        const customContactId = this.selectedAddressBookContact?.id ?? null;
        const subType = this.form.controls.subType.getRawValue() || '';
        const isDoctor = subType == ContactTypeEnum.doctor;

        if (!this.userSelectedFromExisting) {
            const existingUserIdBeforeSearch = this.existingIdByEmail;
            await this.searchExistingByEmail();
            if (customContactId === null && existingUserIdBeforeSearch !== this.existingIdByEmail) {
                return;
            }
            userId = this.existingIdByEmail;

            if (!userId) {
                const user = await this.store2.api.createUser({
                    email: this.form.value.email || null,
                    name: this.form.value.name || '',
                    surname: this.form.value.surname || '',
                    titleBeforeName: this.form.value.titleBeforeName || null,
                    titleAfterName: this.form.value.titleAfterName || null,
                    mobile: this.form.value.mobile || null,
                    phone: this.form.value.phone || null,
                    address: removeTypename(this.form.value.address),
                    password: null,
                    doctor: isDoctor,
                    icpOfDoctor: this.form.value.icpOfDoctor || null,
                    doctorSpecialization: isDoctor ? this.form.value.doctorSpecialization : null,
                    organizationId: await this.store.get(this.store.globalStore!.select(selectCurrentOrganizationId)),
                });
                userId = user?.id || '';
            }
        }

        if (userId) {
            const folder = (await this.store.get(this.store.globalStore!.select(selectFolder)));
            let doctorWorkplace = null;

            if (!folder) {
                return;
            }

            if (isDoctor) {
                if (this.form.value.doctorWorkplaceId == -1) {
                    doctorWorkplace = {
                        userId,
                        ...JSON.parse(this.form.value.doctorWorkplace || '{}'),
                    };
                } else if (this.form.value.doctorWorkplaceId !== null) {
                    const selDoctorWorkplace = this.store2.userWorkplaces().find((x: UserWorkplaceObject) => x.id === this.form.value.doctorWorkplaceId);
                    if (selDoctorWorkplace) {
                        doctorWorkplace = removeTypename({
                            userId,
                            ...selDoctorWorkplace,
                            address: removeTypename(selDoctorWorkplace.address),
                            hours: (selDoctorWorkplace.hours || []).map((hour: any) => removeTypename(hour)),
                        });

                        delete doctorWorkplace.id;
                    }
                }
            }

            await this.store2.api.createPersonContact({
                folderId: Number(folder.id),
                personId: Number(folder.person.id),
                userId,
                type: PersonContactTypeEnum.CONTACT,
                subType,
                validFrom: null,
                validTo: null,
                note: this.form.value.internNote || null,
                relation: this.form.value.clientRelation || '',
                data: {
                    circumstance: this.form.value.contactCircumstance || null,
                    info: this.form.value.info || null,
                    purposeOfAuthorization: this.form.value.purposeOfAuthorization || null,
                    scopeOfAuthorization: this.form.value.scopeOfAuthorization || null,
                    dateOfAuthorization: this.form.value.dateOfAuthorization?.toISOString() || null,
                    authorizationFrom: this.form.value.authorizationFrom?.toISOString() || null,
                    authorizationTo: this.form.value.authorizationTo?.toISOString() || null,
                    doctorSpecializationCode: isDoctor ? this.form.value.doctorSpecialization || null : null,
                    doctorWorkplaceId: null,
                },
                doctorWorkplace,
            });

            if (customContactId !== null) {
                await this.store2.api.deleteAddressBookContact({ id: customContactId });
            }

            this.dialogRef.close({ userId, refresh: true });
        }
    }

    private async updateContact() {
        const systemic = this.contact?.user?.systemic;
        const userId = this.contact?.user?.id;
        const subType = this.form.controls.subType.getRawValue() || '';
        const isDoctor = subType == ContactTypeEnum.doctor;
        if (!systemic && userId) {
            await this.store2.api.updateUser({
                id: userId,
                email: this.form.value.email,
                name: this.form.value.name,
                surname: this.form.value.surname,
                titleBeforeName: this.form.value.titleBeforeName,
                titleAfterName: this.form.value.titleAfterName,
                mobile: this.form.value.mobile,
                phone: this.form.value.phone || null,
                address: removeTypename(this.form.value.address),
                doctor: isDoctor,
                icpOfDoctor: this.form.value.icpOfDoctor || null,
                doctorSpecialization: isDoctor ? this.form.value.doctorSpecialization : null,
            });
        }

        this.store2.api.updatePersonContact({
            id: this.contact!.id,
            subType,
            validFrom: this.contact!.validFrom,
            validTo: this.contact!.validTo,
            note: this.form.value.internNote || null,
            relation: this.form.value.clientRelation || '',
            data: {
                circumstance: this.form.value.contactCircumstance || null,
                info: this.form.value.info || null,
                purposeOfAuthorization: this.form.value.purposeOfAuthorization || null,
                scopeOfAuthorization: this.form.value.scopeOfAuthorization || null,
                dateOfAuthorization: this.form.value.dateOfAuthorization?.toISOString() || null,
                authorizationFrom: this.form.value.authorizationFrom?.toISOString() || null,
                authorizationTo: this.form.value.authorizationTo?.toISOString() || null,
                doctorSpecializationCode: this.form.value.doctorSpecialization || null,
                doctorWorkplaceId: this.form.value.doctorWorkplaceId || null,
            },
            doctorWorkplace: (isDoctor && this.form.value.doctorWorkplaceId == -1) ? {
                userId,
                ...JSON.parse(this.form.value.doctorWorkplace || '{}'),
            } : undefined,
        }).then(() => this.dialogRef.close({ userId, refresh: true }));
    }

    openExistingUserSelection() {
        this.nameMatchOptions = this.existingUsersByName;

        const ref = this.dialogService.open(
            PersonContactUpsertNameMatchDialogComponent,
            {
                header: translate.instant('inputs.selectUserForm.title'),
                data: {
                    users: this.existingUsersByName,
                },
                styleClass: 'p-dialog-width-25',
            },
        );

        ref?.onClose.subscribe((res) => {
            if (res && res.user) {
                this.existingUsersByName = [];
                this.existingUserSelected = res.user;
                this.enableUserEdit = !(this.existingUserSelected?.systemic ?? false);
                this.form.patchValue({
                    existingUserId: this.existingUserSelected?.id ?? null,
                    titleBeforeName: this.existingUserSelected?.titleBeforeName ?? null,
                    name: this.existingUserSelected?.name ?? null,
                    surname: this.existingUserSelected?.surname ?? null,
                    titleAfterName: this.existingUserSelected?.titleAfterName ?? null,
                    mobile: this.existingUserSelected?.mobile ?? null,
                    phone: this.existingUserSelected?.phone ?? null,
                    email: this.existingUserSelected?.email ?? null,
                    address: this.existingUserSelected?.address ?? null,
                    icpOfDoctor: this.existingUserSelected?.icpOfDoctor ?? null,
                });
            }
        });
    }

    async searchExistingByEmail(email = this.form.value.email): Promise<void> {
        if (!email) {
            this.selectExisting([]);
            return;
        }

        const users = await this.store2.api.getUsers(FilterBuilder.filter({ email }));
        if (this.form.value.email !== email) return;

        this.selectExisting(users?.items || []);
    }

    async searchExistingByName() {
        const name = this.form.value.name;
        const surname = this.form.value.surname;
        if (name && surname) {
            this.store2.api.getUsers(FilterBuilder.filter({
                name: name,
                surname: surname,
            }), userGridItemFrag).then(users => {
                this.existingUsersByName =
                    (users?.['items'] || []).filter(
                        (user: UserGridItemObject) =>
                            !user.userLinks || !user.userLinks.some(x => x.linkType === UserLinkTypeEnum.SERVICE_PERSON_CONTACT),
                    ) ?? [];
            });
        }
    }

    selectExisting(users: UserGridItemObject[]) {
        if (!users.length) {
            this.existingNameByEmail = '';
            this.existingIdByEmail = '';
            return;
        }
        const user = users[0];
        this.existingNameByEmail = formatPerson(user);
        this.existingIdByEmail = user.id;
    }

    navigateToPersonDetail(): void {
        this.router.navigateByUrl(userUri(this.existingIdByEmail)).then(() => this.dialogRef.close());
    }

    private updateValidators() {
        const contactType = this.form.controls.subType.getRawValue();
        if (contactType === ContactTypeEnum.contact) {
            this.form.controls.clientRelation.setValidators(Validators.required);
        } else {
            this.form.controls.clientRelation.clearValidators();
        }
        if (contactType === ContactTypeEnum.authorized) {
            this.form.controls.purposeOfAuthorization.setValidators(Validators.required);
        } else {
            this.form.controls.purposeOfAuthorization.clearValidators();
        }
        if (!this.contact && !this.contactSelectedFromExisting) {
            this.form.controls.email.setValidators([Validators.email]);
            this.form.controls.email.setAsyncValidators([this.distinctEmail()]);
        } else {
            this.form.controls.email.clearValidators();
            this.form.controls.email.clearAsyncValidators();
        }
        this.form.controls.clientRelation.updateValueAndValidity();
        this.form.controls.purposeOfAuthorization.updateValueAndValidity();

        if (this.contact || this.contactSelectedFromExisting) {
            this.form.controls.email.updateValueAndValidity();
        } else {
            this.form.updateValueAndValidity();
        }
    }

    private async isEmailAlreadyUsedInUse(
        email: string | null,
    ) {
        if (email && email.length) {
            const users = await this.store2.api.getUsers(FilterBuilder.filter({ email }));
            return !!users?.['items'].length;
        }

        return false;
    }

    private distinctEmail(): AsyncValidatorFn {
        return async (control: AbstractControl) => {
            if (control.value) {
                return (await this.isEmailAlreadyUsedInUse(control.value))
                    ? FieldErrorFactory.emailAlreadyUsedError()
                    : null;
            }
            return null;
        };
    }

    isDoctorAllowed(doctor: string | null = null): boolean {
        const d = doctor === null ? this.form.controls.subType.getRawValue() : doctor;
        if (d === ContactTypeEnum.doctor && this.selectedUser !== null) {
            if (!this.selectedUser.doctor) {
                this.deselect();
                return false;
            }
        }

        return true;
    }

    deselect() {
        this.form.patchValue({
            existingUserId: null,
            customContactId: null,
        });
        this.selectedAddressBookContact = null;
        this.selectedAddressBookContactName = '';
        this.updateFormWithSelectedUser(null);
    }

    updateFormWithSelectedUser(user: UserGridItemObject | null) {
        if (this.selectExistingModal) this.selectExistingModal.show(false);
        this.selectedUser = user;
        this.selectedAddressBookContact = null;
        this.selectedAddressBookContactName = '';
        if (!this.isDoctorAllowed()) return;

        this.form.patchValue({
            existingUserId: user?.id ?? null,
            customContactId: null,
            titleBeforeName: user?.titleBeforeName ?? null,
            name: user?.name ?? null,
            surname: user?.surname ?? null,
            titleAfterName: user?.titleAfterName ?? null,
            email: user?.email ?? null,
            phone: user?.phone ?? null,
            mobile: user?.mobile ?? null,
            address: user?.address ?? null,
            icpOfDoctor: user?.icpOfDoctor ?? null,
        });
    }

    public fetchUserWorkplaces(userId: string): void {
        this.store2.api.getUserWorkplaces(FilterBuilder.filter({ userId })).then((wp) => this.store2.userWorkplaces.set(wp?.['items'] || []));
    }

    protected async openWorkplaceDialog(workplaceId: number | null = null) {
        let workplace: UserWorkplaceObject | null = null;
        if (workplaceId) {
            const workplaces = await this.store2.api.getUserWorkplaces(FilterBuilder.filter({ id: workplaceId }));
            workplace = workplaces?.items?.[0];
        }

        const ref = this.dialogService.open(UserWorkplaceUpsertDialogComponent, {
            header: translate.instant('user.workplace.labelMulti'),
            data: {
                item: workplace,
            },
        });

        ref?.onClose.subscribe((res) => {
            if (res && res.data) {
                this.store2.newWorkplace.set({ ...res.data, id: -1 });
                this.form.patchValue({
                    doctorWorkplaceId: -1,
                    doctorWorkplace: JSON.stringify(res.data),
                });
            }
        });
    }

    protected async select(_event: Event) {
        const userId = this.form.value.existingUserId;
        if (userId) {
            const res = await this.store2.api.getUsers(FilterBuilder.filter({ id: userId }));
            this.updateFormWithSelectedUser(res?.items?.[0]);
            return;
        }

        const customContactId = this.form.value.customContactId;
        if (!customContactId) return;

        const res = await this.store2.api.getAddressBookContacts(
            FilterBuilder.singleItem({ id: customContactId }),
            addressBookContactFullDetailFrag,
        );
        const contact = res?.items?.[0];
        if (contact) this.updateFormWithSelectedAddressBookContact(contact);
    }

    private updateFormWithSelectedAddressBookContact(contact: AddressBookContactObject): void {
        if (this.selectExistingModal) this.selectExistingModal.show(false);

        const informations = contact.informations || [];
        const email = contact.email
            || informations.find((item) => item.type === AddressBookInformationTypeEnum.EMAIL)?.info
            || null;
        const mobile = informations.find((item) =>
            item.type === AddressBookInformationTypeEnum.PHONE && item.subtype === 'mobile',
        )?.info || null;
        const phone = informations.find((item) =>
            item.type === AddressBookInformationTypeEnum.PHONE && item.subtype !== 'mobile',
        )?.info || null;
        const addressValue = informations.find((item) => item.type === AddressBookInformationTypeEnum.ADDRESS)?.info;
        const icp = informations.find((item) => item.type === AddressBookInformationTypeEnum.ICP)?.info || null;

        this.selectedUser = null;
        this.selectedAddressBookContact = contact;
        this.selectedAddressBookContactName = [
            contact.titleBeforeName,
            contact.name || contact.company,
            contact.surname,
            contact.titleAfterName,
        ].filter(Boolean).join(' ');
        this.enableUserEdit = true;
        this.isUserSelected = true;

        this.form.patchValue({
            existingUserId: null,
            customContactId: contact.id,
            titleBeforeName: contact.titleBeforeName,
            name: contact.name || contact.company,
            surname: contact.surname,
            titleAfterName: contact.titleAfterName,
            mobile,
            phone,
            email,
            address: this.parseAddressBookAddress(addressValue),
            icpOfDoctor: icp,
        });
        this.updateValidators();
    }

    private parseAddressBookAddress(value?: string): AddressDetailObject | null {
        if (!value) return null;

        try {
            return JSON.parse(value) as AddressDetailObject;
        } catch {
            return null;
        }
    }

    isAllowed = isAllowed;
    contactTypeEnumOptions = contactTypeEnumOptions;
}
